import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { select } from 'd3-selection';
import 'd3-transition';
import { D3ZoomEvent, zoom, zoomIdentity, zoomTransform, ZoomTransform } from 'd3-zoom';

@Component({
  selector: 'app-tree-view',
  imports: [NgTemplateOutlet],
  templateUrl: './tree-view.component.html',
  styleUrl: './tree-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tree-view size-full block relative overflow-hidden',
  },
})
export class TreeViewComponent<T> implements OnInit {
  private readonly _el = inject<ElementRef<Element>>(ElementRef);
  private readonly _scrollArea =
    viewChild.required<ElementRef<HTMLDivElement>>('scrollArea');

  private readonly _zoomInstance = zoom();

  public readonly nodeTemplate = contentChild<TemplateRef<unknown>>('nodeTemplate');
  public readonly rootTemplate = contentChild<TemplateRef<unknown>>('rootTemplate');
  public readonly nodes = input<T[]>([]);

  public readonly rootId = input<string>();

  public readonly minZoom = input(0.1);
  public readonly maxZoom = input(3);
  public readonly zoomStep = input(0.1);

  protected readonly zoom = signal({
    x: 0,
    y: 0,
    k: 1,
  });

  public readonly currentScale = computed(() => this.zoom().k);

  ngOnInit() {
    const container = this._el.nativeElement;

    const d3Zoom = this._zoomInstance
      .scaleExtent([this.minZoom(), this.maxZoom()])
      .clickDistance(5)
      .filter((event) => {
        if (event.type === 'wheel') {
          event.preventDefault();
        }

        return true;
      })
      .on('zoom', (event: D3ZoomEvent<Element, unknown>) => {
        this._applyZoom(event.transform);
      });

    select(container).call(d3Zoom);
  }

  center() {
    const container = this._el.nativeElement;
    const scrollArea = this._scrollArea().nativeElement;

    const { clientWidth } = container;
    const { offsetWidth } = scrollArea;

    const initialTransform = zoomIdentity
      .translate(clientWidth / 2 - offsetWidth / 2, 32)
      .scale(1);

    select(container).call(this._zoomInstance.transform, initialTransform);
  }

  centerHorizontally() {
    const container = this._el.nativeElement;
    const scrollArea = this._scrollArea().nativeElement;

    const { clientWidth } = container;
    const { offsetWidth } = scrollArea;

    const initialTransform = zoomIdentity
      .translate(clientWidth / 2 - offsetWidth / 2, 32)
      .scale(zoomIdentity.k);

    select(container).call(this._zoomInstance.transform, initialTransform);
  }

  centerVertically() {
    const container = this._el.nativeElement;
    const scrollArea = this._scrollArea().nativeElement;

    const { clientHeight } = container;
    const { offsetHeight } = scrollArea;

    const initialTransform = zoomIdentity
      .translate(32, clientHeight / 2 - offsetHeight / 2)
      .scale(zoomIdentity.k);

    select(container).call(this._zoomInstance.transform, initialTransform);
  }

  fitBounds() {
    const padding = 32;
    const container = this._el.nativeElement;
    const scrollArea = this._scrollArea().nativeElement;

    const { clientWidth, clientHeight } = container;
    const { offsetWidth, offsetHeight } = scrollArea;

    if (offsetWidth === 0 || offsetHeight === 0) return;

    const scaleX = (clientWidth - padding * 2) / offsetWidth;
    const scaleY = (clientHeight - padding * 2) / offsetHeight;

    const scale = Math.min(scaleX, scaleY, this.maxZoom());
    const finalScale = Math.max(scale, this.minZoom());

    const x = (clientWidth - offsetWidth * finalScale) / 2;
    const y = (clientHeight - offsetHeight * finalScale) / 2;

    const fitTransform = zoomIdentity.translate(x, y).scale(finalScale);

    select(container).call(this._zoomInstance.transform, fitTransform);
  }

  resetZoom() {
    select(this._el.nativeElement).call(this._zoomInstance.scaleTo, 1);
  }

  zoomIn() {
    const currentTransform = zoomTransform(this._el.nativeElement);
    const newScale = currentTransform.k + this.zoomStep();

    select(this._el.nativeElement).call(this._zoomInstance.scaleTo, newScale);
  }

  zoomOut() {
    const currentTransform = zoomTransform(this._el.nativeElement);
    const newScale = currentTransform.k - this.zoomStep();

    select(this._el.nativeElement).call(this._zoomInstance.scaleTo, newScale);
  }

  panToNode(nodeId: string) {
    const scrollArea = this._scrollArea().nativeElement;
    const targetEl = scrollArea.querySelector(
      `[data-unit-id="${nodeId}"]`
    ) as HTMLElement | null;

    if (!targetEl) return;

    const container = this._el.nativeElement;
    const { clientWidth, clientHeight } = container;

    // Get the target element's position relative to the scroll area
    const targetRect = targetEl.getBoundingClientRect();
    const scrollRect = scrollArea.getBoundingClientRect();
    const currentTransform = zoomTransform(container);

    // Calculate center of target in unscaled scroll area coordinates
    const targetCenterX =
      (targetRect.left - scrollRect.left) / currentTransform.k +
      targetRect.width / (2 * currentTransform.k);
    const targetCenterY =
      (targetRect.top - scrollRect.top) / currentTransform.k +
      targetRect.height / (2 * currentTransform.k);

    // Always zoom to 100% for ideal viewing
    const scale = 1;
    const x = clientWidth / 2 - targetCenterX * scale;
    const y = clientHeight / 2 - targetCenterY * scale;

    const newTransform = zoomIdentity.translate(x, y).scale(scale);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (select(container) as any)
      .transition()
      .duration(1000)
      .call(this._zoomInstance.transform, newTransform);
  }

  private _applyZoom(transform: ZoomTransform) {
    this.zoom.update((currentState) => ({
      ...currentState,
      ...transform,
    }));
  }
}
